import { FormType, IdKindType, OpenType, RouterName, SupplementIdentityType } from '@/enum'

/** 投资者三字段仅适用于普通开户 */
export function isRegularOpenAccount (formType?: string | null): boolean {
  return formType === FormType.OPEN_ACCOUNT
}

/** 后端 int：0/1；null 表示历史草稿未赋值 */
export type InvestorIntFlag = 0 | 1 | null

/** 后端 string：cn-中国大陆，others-非中国大陆 */
export type InvestorNationalityCode = 'cn' | 'others' | null

export interface InvestorDraftFields {
  isMainLandInvestor: InvestorIntFlag
  isCsrcChinaConfirmed: InvestorIntFlag
  investorNationalityCode: InvestorNationalityCode
}

/** openInfoSave 入参中的三字段类型 */
export interface InvestorFieldsForApiSave {
  isMainLandInvestor: 0 | 1
  isCsrcChinaConfirmed: 0 | 1
  investorNationalityCode: 'cn' | 'others'
}

const INVESTOR_INT_KEYS = ['isMainLandInvestor', 'isCsrcChinaConfirmed'] as const
const NATIONALITY_CODE_KEY = 'investorNationalityCode' as const

export const INVESTOR_INT_YES = 1
export const INVESTOR_INT_NO = 0
/** 中国大陆 */
export const INVESTOR_NATIONALITY_CN = 'cn'
/** 非中国大陆 */
export const INVESTOR_NATIONALITY_OTHERS = 'others'

const INVESTOR_FIELDS_NON_MAINLAND: InvestorFieldsForApiSave = {
  isMainLandInvestor: INVESTOR_INT_NO,
  isCsrcChinaConfirmed: INVESTOR_INT_NO,
  investorNationalityCode: INVESTOR_NATIONALITY_OTHERS
}

/** 规范 isMainLandInvestor / isCsrcChinaConfirmed（int，兼容历史 '0'|'1'） */
export function normalizeInvestorIntFlag (value: unknown): 0 | 1 | undefined {
  if (value === 0 || value === '0') {
    return INVESTOR_INT_NO
  }
  if (value === 1 || value === '1') {
    return INVESTOR_INT_YES
  }
  return undefined
}

/** 规范 investorNationalityCode（兼容历史 '0'/'1' 与 int） */
export function normalizeNationalityCode (value: unknown): 'cn' | 'others' | undefined {
  if (value === INVESTOR_NATIONALITY_CN || value === '1' || value === 1) {
    return INVESTOR_NATIONALITY_CN
  }
  if (value === INVESTOR_NATIONALITY_OTHERS || value === '0' || value === 0) {
    return INVESTOR_NATIONALITY_OTHERS
  }
  return undefined
}

function isInvestorIntFlagSet (value: unknown): boolean {
  return normalizeInvestorIntFlag(value) !== undefined
}

function isNationalityCodeSet (value: unknown): boolean {
  return normalizeNationalityCode(value) !== undefined
}

function normalizeIdKind (value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  const num = Number(value)
  return Number.isFinite(num) ? num : undefined
}

function normalizeSupplementIdentityType (value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  const num = Number(value)
  if (num === SupplementIdentityType.MAINLAND || num === SupplementIdentityType.NON_MAINLAND) {
    return num
  }
  return undefined
}

export function isCsrcChinaConfirmedDone (openInfo?: Record<string, unknown> | null): boolean {
  return normalizeInvestorIntFlag(openInfo?.isCsrcChinaConfirmed) === INVESTOR_INT_YES
}

/** 三个投资者标记字段未全部齐全 */
export function isInvestorFieldsUnset (openInfo?: Record<string, unknown> | null): boolean {
  if (!openInfo) {
    return true
  }
  return !(
    isInvestorIntFlagSet(openInfo.isMainLandInvestor) &&
    isInvestorIntFlagSet(openInfo.isCsrcChinaConfirmed) &&
    isNationalityCodeSet(openInfo.investorNationalityCode)
  )
}

export function isMainlandIdDocumentType (
  openInfo: Record<string, unknown>,
  openType: number
): boolean {
  return (
    openType === OpenType.MAINLAND ||
    normalizeIdKind(openInfo.idKind) === IdKindType.MAINLAND_ID_CARD
  )
}

export function isHkTemporaryMainlandDocumentType (
  openInfo: Record<string, unknown>
): boolean {
  if (normalizeIdKind(openInfo.idKind) !== IdKindType.HK_TEMPORARY_ID_CARD) {
    return false
  }
  const supplementType = normalizeSupplementIdentityType(openInfo.supplementIdentityType)
  // 未写入时按大陆居民（UI 默认 + 历史草稿）
  return supplementType !== SupplementIdentityType.NON_MAINLAND
}

/** 大陆身份证或港非永久+中国大陆居民，需完整投资者三字段 */
export function isMainlandInvestorDocumentScenario (
  openInfo: Record<string, unknown>,
  openType: number
): boolean {
  return (
    isMainlandIdDocumentType(openInfo, openType) ||
    isHkTemporaryMainlandDocumentType(openInfo)
  )
}

/** 转账提交前：仅普通开户且大陆/港非永久+大陆居民且三字段缺失时拦截 */
export function shouldBlockSubmitForMissingInvestorFields (
  openInfo: Record<string, unknown> | null | undefined,
  openType: number,
  formType?: string | null
): boolean {
  if (!openInfo || !isRegularOpenAccount(formType)) {
    return false
  }
  return (
    isMainlandInvestorDocumentScenario(openInfo, openType) &&
    isInvestorFieldsUnset(openInfo)
  )
}

export interface BuildDeclarationInvestorFieldsOptions {
  openType: number
  declarationFrom?: string
  idKind?: number
  supplementIdentityType?: number
}

/** 内地客户声明页签署后写入的三个投资者标记 */
export function buildInvestorDraftFieldsForDeclaration (
  options: BuildDeclarationInvestorFieldsOptions
): InvestorDraftFields {
  const isHkTemporaryMainland =
    options.declarationFrom === RouterName.ID_CARD &&
    options.idKind === IdKindType.HK_TEMPORARY_ID_CARD &&
    options.supplementIdentityType === SupplementIdentityType.MAINLAND

  if (options.openType === OpenType.MAINLAND || isHkTemporaryMainland) {
    return {
      isMainLandInvestor: INVESTOR_INT_YES,
      isCsrcChinaConfirmed: INVESTOR_INT_YES,
      investorNationalityCode: INVESTOR_NATIONALITY_CN
    }
  }

  return {
    isMainLandInvestor: INVESTOR_INT_NO,
    isCsrcChinaConfirmed: INVESTOR_INT_YES,
    investorNationalityCode: INVESTOR_NATIONALITY_OTHERS
  }
}

export const DECLARATION_DRAFT_META_LAND = 'land_decl'
export const DECLARATION_DRAFT_META_OTHERS = 'others_decl'

export function getDeclarationDraftMeta (openType: number): string {
  return openType === OpenType.MAINLAND
    ? DECLARATION_DRAFT_META_LAND
    : DECLARATION_DRAFT_META_OTHERS
}

/** 写入 id-card 等步骤草稿时，保留声明页已落库的投资者字段 */
export function pickDeclarationPersistedFields (
  openInfo?: Record<string, unknown> | null
): Record<string, unknown> {
  if (!openInfo) {
    return {}
  }
  const persisted: Record<string, unknown> = {}
  INVESTOR_INT_KEYS.forEach((key) => {
    const normalized = normalizeInvestorIntFlag(openInfo[key])
    if (normalized === INVESTOR_INT_NO || normalized === INVESTOR_INT_YES) {
      persisted[key] = normalized
    }
  })
  const nationality = normalizeNationalityCode(openInfo[NATIONALITY_CODE_KEY])
  if (nationality === INVESTOR_NATIONALITY_CN || nationality === INVESTOR_NATIONALITY_OTHERS) {
    persisted[NATIONALITY_CODE_KEY] = nationality
  }
  if (openInfo.mainlandDeclarationChecked) {
    persisted.mainlandDeclarationChecked = openInfo.mainlandDeclarationChecked
  }
  return persisted
}

function pickInvestorFieldsFromCache (
  openInfo: Record<string, unknown>
): Partial<InvestorFieldsForApiSave> {
  const cached: Partial<InvestorFieldsForApiSave> = {}
  INVESTOR_INT_KEYS.forEach((key) => {
    const normalized = normalizeInvestorIntFlag(openInfo[key])
    if (normalized === INVESTOR_INT_NO || normalized === INVESTOR_INT_YES) {
      cached[key] = normalized
    }
  })
  const nationality = normalizeNationalityCode(openInfo[NATIONALITY_CODE_KEY])
  if (nationality === INVESTOR_NATIONALITY_CN || nationality === INVESTOR_NATIONALITY_OTHERS) {
    cached.investorNationalityCode = nationality
  }
  return cached
}

/**
 * openInfoSave 提交用
 * - isMainLandInvestor、isCsrcChinaConfirmed：int 0/1
 * - investorNationalityCode：string cn / others
 * - 其他证件类型：0、0、others
 * - 大陆/港非永久+大陆居民且无缓存：null（老用户）
 */
export function resolveInvestorFieldsForOpenInfoSave (options: {
  openInfo?: Record<string, unknown> | null
  openType: number
  formType?: string | null
}): InvestorFieldsForApiSave | null {
  const { openInfo, openType, formType } = options
  if (!openInfo || !isRegularOpenAccount(formType)) {
    return null
  }

  const isMainlandDoc = isMainlandIdDocumentType(openInfo, openType)
  const isHkTempMainlandDoc = isHkTemporaryMainlandDocumentType(openInfo)

  if (!isMainlandDoc && !isHkTempMainlandDoc) {
    return { ...INVESTOR_FIELDS_NON_MAINLAND }
  }

  if (isInvestorFieldsUnset(openInfo)) {
    return null
  }

  const cached = pickInvestorFieldsFromCache(openInfo)

  return {
    isMainLandInvestor: cached.isMainLandInvestor ?? INVESTOR_INT_YES,
    isCsrcChinaConfirmed: cached.isCsrcChinaConfirmed ?? INVESTOR_INT_YES,
    investorNationalityCode: cached.investorNationalityCode ?? INVESTOR_NATIONALITY_CN
  }
}
